import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useSelector } from 'react-redux';
import { ENTERPRISE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import styled from 'styled-components';
import { useAppSelector } from '../../reducers';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors';
import { calculatedPriceForProductConfigurationsInListSelector } from '../../selectors/PricingSelectors';
import { productLevelForCoreSeatsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasHigherProductLevelInProductInventorySelector, hasProductLineInBundledProductInventorySelector, hasProductLineSelectorInProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { coreSeatSkuForProductLevelSelector, includedCoreSeatsQuantityForProductVersionSelector, includedSeatQuantityBySkuIdSelector, isQuantifiableHubSelector, productForSkuIdSelector } from '../../selectors/ProductsSelectors';
import PriceDisplay from '../common/pricing/PriceDisplay';
import { SALES_SEAT, SERVICE_SEAT } from 'self-service-api/constants/UnitNames';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledMessage = styled(FormattedMessage).withConfig({
  displayName: "IncludedSeatsDisplay__StyledMessage",
  componentId: "sc-15bl70p-0"
})(["font-weight:600;display:block;"]);
const IncludedSeatsDisplay = ({
  skuId,
  quantityPackSkuId,
  quantityPackQuantity,
  queryParamTerm
}) => {
  const productConfigurations = [createProductConfig({
    skuId
  })];
  if (quantityPackSkuId && quantityPackQuantity && quantityPackQuantity > 0) productConfigurations.push(createProductConfig({
    skuId: quantityPackSkuId,
    quantity: quantityPackQuantity
  }));
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const includedCoreSeats = useAppSelector(state => {
    return includedCoreSeatsQuantityForProductVersionSelector(state, {
      productVersionSkuId: skuId
    });
  });
  const includedSalesSeats = useAppSelector(state => {
    return includedSeatQuantityBySkuIdSelector(state, {
      skuId,
      seatUnitName: SALES_SEAT
    });
  });
  const includedServiceSeats = useAppSelector(state => {
    return includedSeatQuantityBySkuIdSelector(state, {
      skuId,
      seatUnitName: SERVICE_SEAT
    });
  });
  const includedSeats = includedCoreSeats + includedSalesSeats + includedServiceSeats;
  const {
    apiName,
    productLine,
    productLevel
  } = useSelector(state => {
    return productForSkuIdSelector(state, {
      skuId
    });
  });
  const ownsProductLineViaProducts = useAppSelector(state => {
    return hasProductLineSelectorInProductInventorySelector(state, {
      productLine
    });
  });
  const ownsProductLineViaBundle = useAppSelector(state => {
    return hasProductLineInBundledProductInventorySelector(state, {
      productLine
    });
  });
  const ownsProductLine = ownsProductLineViaProducts || ownsProductLineViaBundle;
  const isQuantifiableHub = useAppSelector(state => isQuantifiableHubSelector(state, {
    skuId
  }));
  const currencyCode = useAppSelector(activeCurrencySelector);
  const coreSeatTierOwned = useAppSelector(state => productLevelForCoreSeatsSelector(state, {
    productConfigurations: []
  }));
  const additionalSeatsPrice = useAppSelector(state => {
    if (!isUngatedForSeats) return 0;

    // core seat product level owned or merch card's product level, whichever higher
    const productLevelForCoreSeats = productLevelForCoreSeatsSelector(state, {
      productConfigurations
    });
    const coreSeatSku = coreSeatSkuForProductLevelSelector(state, {
      productLevel: productLevelForCoreSeats
    });
    if (!coreSeatSku) return 0;
    return calculatedPriceForProductConfigurationsInListSelector(state, {
      allProductConfigurations: productConfigurations,
      productConfigurations: [createProductConfig({
        skuId: coreSeatSku.skuId
      })],
      queryParamTerm
    }).monthly.calculatedPrice;
  });
  const showPricePerMonthPerSeat = isUngatedForSeats && (isQuantifiableHub || !isQuantifiableHub && (
  // If the highest owned tier is free or starter, show the Starter price in "/mo/seat" format on ALL hubs
  coreSeatTierOwned === STARTER && productLevel === STARTER ||
  // If looking at merch card of a hub OWNED, show Starter price in "/mo/seat" format
  ownsProductLine && productLevel === STARTER));
  const showIncludedSeats = isUngatedForSeats && !isQuantifiableHub && (
  // Show "Included Y seats" on ALL Non-quantifiable hubs' Pro/Ent card
  productLevel === PROFESSIONAL || productLevel === ENTERPRISE ||
  // Show "Included Y seats" on Starter cards only if they DO NOT OWN HUB [and NOT in "/mo/seat" format]
  productLevel === STARTER && !ownsProductLine && !showPricePerMonthPerSeat);
  const isDowngrade = useAppSelector(state => {
    return hasHigherProductLevelInProductInventorySelector(state, {
      productLine,
      productLevel
    });
  });

  // Show "Additional Seats start at $X/mo" for all non-quantifiable hubs' cards that are NOT downgrades and NOT in the "/mo/seat" format
  const showAdditionalSeatsPriceSubtext = isUngatedForSeats && additionalSeatsPrice > 0 && !isQuantifiableHub && !isDowngrade && !showPricePerMonthPerSeat;
  return /*#__PURE__*/_jsxs("div", {
    className: "m-top-6",
    children: [showIncludedSeats ? /*#__PURE__*/_jsx(StyledMessage, {
      message: `merchCards.pricing.includes.seats.${productLevel}`,
      options: {
        count: includedSeats
      }
    }) : null, showAdditionalSeatsPriceSubtext ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "merchCards.pricing.subtexts.additionalSeatsStartAt_jsx",
      options: {
        seleniumId: apiName,
        currencyCode,
        additionalSeatsPrice
      },
      elements: {
        PriceDisplay
      }
    }) : null]
  });
};
export default IncludedSeatsDisplay;